import api, { fetchData } from './api';
import EmailLog from '../types/emailLog';
import { MinhaCopiaResumo, MinhaCopiaDetalhe } from '../types/minhaCopia';

interface ApiResultVM<T> {
  data: T | null;
  messages: string[];
}

export interface EmailLogListFilter {
  somenteComErro?: boolean;
  pageNum?: string;
  quantityPerPage?: string;
  sort?: string;
  order?: string;
}

export const getListaEmailLog = async (filters: EmailLogListFilter) => {
  try {
    const endpoint = '/EmailLog/list';

    const params: Record<string, any> = {
      somenteComErro: filters?.somenteComErro ?? false,
      'paginacao.pageNum': filters?.pageNum ? Number(filters.pageNum) + 1 : 1,
      'paginacao.quantityPerPage': filters?.quantityPerPage || '40',
      'paginacao.order': filters?.order,
      'paginacao.sort': filters?.sort,
    };

    for (const key of Object.keys(params)) {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        delete params[key];
      }
    }

    return await fetchData<EmailLog>(endpoint, params);
  } catch (error) {
    console.error('Erro ao buscar lista de emails enviados:', error);
    throw error;
  }
};

export const reenviarEmailLog = async (id: number) => {
  try {
    const response = await api.post(`/EmailLog/reenviar/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao reenviar email com ID ${id}:`, error);
    throw error;
  }
};

export interface MinhasCopiasListFilter {
  pageNum?: string;
  quantityPerPage?: string;
  sort?: string;
  order?: string;
}

/**
 * Busca, de forma paginada, os e-mails de liberação em que o usuário logado (ou
 * todos, se ROLE_MASTER) foi copiado (Cco) - módulo "Minhas Cópias", disponível
 * para qualquer usuário autenticado (diferente de getListaEmailLog acima, que é a
 * auditoria completa e exige ROLE_MASTER). Mesmo padrão de paginação.
 */
export const getListaMinhasCopias = async (filters: MinhasCopiasListFilter) => {
  try {
    const endpoint = '/MinhasCopias/list';

    const params: Record<string, any> = {
      'paginacao.pageNum': filters?.pageNum ? Number(filters.pageNum) + 1 : 1,
      'paginacao.quantityPerPage': filters?.quantityPerPage || '40',
      'paginacao.order': filters?.order,
      'paginacao.sort': filters?.sort,
    };

    for (const key of Object.keys(params)) {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        delete params[key];
      }
    }

    return await fetchData<MinhaCopiaResumo>(endpoint, params);
  } catch (error) {
    console.error('Erro ao buscar e-mails em cópia:', error);
    throw error;
  }
};

/**
 * Busca o detalhe de uma cópia (Cco): os campos de negócio da liberação original em
 * formato de campo, mais a cópia fiel do HTML enviado e a elegibilidade de reenvio.
 * Só o dono do e-mail (estava no Cco) ou master (ver MinhasCopiasController).
 */
export const getDetalheMinhaCopia = async (id: number): Promise<MinhaCopiaDetalhe> => {
  const response = await api.get<ApiResultVM<MinhaCopiaDetalhe>>(`/MinhasCopias/${id}`);

  if (!response.data?.data) {
    throw new Error('Detalhe da cópia veio sem dados.');
  }

  return response.data.data;
};

/**
 * Reenvia uma cópia (Cco) - só dentro da janela de 24h do envio original, e só o dono
 * do e-mail (estava no Cco) ou master. Reaproveita o mesmo disparo do reenvio de
 * auditoria (reenviarEmailLog acima), com autorização diferente no back-end.
 */
export const reenviarMinhaCopia = async (id: number) => {
  try {
    const response = await api.post(`/MinhasCopias/${id}/reenviar`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao reenviar cópia com ID ${id}:`, error);
    throw error;
  }
};
